import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { catchError, map } from 'rxjs/operators';
import {Observable, of, throwError} from 'rxjs';
import { Router } from '@angular/router';

import { AppSettings } from '../app.settings';
import { Response } from '../models/response.model';
import { AppDate } from '../models/date.model';
import { ProductCatalog } from '../models/product-catalog.model';
import { CacheModel } from '../models/cache.model';
import { Product } from '../models/product.model';
import {FilterGroup} from '../models/filter-group.model';
import {IDecisionPointData} from '../interfaces/decision-point.data';
import {IDecisionPointDelivery} from '../interfaces/decision-point-delivery';
import {DetailedError, findDetailedErrorMessage} from '../shared/helpers';
import {isEmptyArray, transformFilters} from '../shared/utils';
import {ProductCategory} from '../models/product-category.model';
import {Brand} from '../models/brand.model';
import {ProductResult} from '../interfaces/product.result';
import {API_URL} from '../constants/api-urls';
import { LocalStorageService } from './local-storage.service';
import {AllocationGroup, InventoryAllocation, ProductInventoryAllocations} from '../interfaces/allocation-group';

const BASE_URL = '/products';

export type ProductResponseShape = 'full' | 'brief';

export interface AggregateQuantities {
    [productId: string]: {sku_id: number; sku: number; group?: number}[];
}

interface V2CatalogItem {
    content?: {category?: string; data?: unknown}[] | Record<string, unknown>;
    attrs?: {attr?: string; value?: string}[];
    [key: string]: unknown;
}

export interface ProductStatistics {
    ow_upcoming?: {
        label: string;
        start_at: AppDate;
        end_at: AppDate;
    };
}

@Injectable()
export class ApiProductService {
    public catalogCache: CacheModel;
    readonly apiURL: string;
    readonly  apiV2URL: string;

    constructor(
        protected http: HttpClient,
        protected appSettings: AppSettings,
        protected toastr: ToastrService,
        protected router: Router,
        protected localStorageService: LocalStorageService
    ) {
        this.catalogCache = new CacheModel();
        this.apiURL = this.appSettings.settings.apiBaseURL + BASE_URL;
        this.apiV2URL = `${API_URL}${BASE_URL}`;
    }

    public reset(): void {
        this.catalogCache.clear();
    }

    public getStatistics(orderWindowId: number): Observable<ProductStatistics> {
      const url = `${this.apiURL}/statistics/${orderWindowId}`;
      // const url = `${API_URL}${BASE_URL}/statistics/${orderWindowId}`;
        return this.http.get<Response>(url)
            .pipe(
                map(response => {
                  if (response.data) {
                      const result = response.data;

                      if (result.ow_upcoming) {
                          result.ow_upcoming.start_at = new AppDate(result.ow_upcoming.start_at);
                          result.ow_upcoming.end_at = new AppDate(result.ow_upcoming.end_at);
                      }
                      return result;
                  }

                  return null;
                }),
                catchError(this.handleError('ProductService::getStatistics', {}))
            );

    }

    public getProductCatalog(slugOrID: string | number = null): Observable<ProductCatalog> {
        if (this.catalogCache.has(slugOrID)) {
            return of(new ProductCatalog(this.catalogCache.get(slugOrID)));
        }
        const url = `${this.apiV2URL}/catalog/${slugOrID}`;
        return this.http.get<Response>(url)
            .pipe(
                map(response => {

                    if (response.data) {
                        const result = response.data;

                        this.catalogCache.set(result.id, result);
                        this.catalogCache.set(slugOrID, result);

                        return new ProductCatalog(result);
                    }

                    return null;
                }),
                catchError(this.handleError('ProductService::getCatalog', null))
            );
    }


    public getCatalogsIds(windowID: number = null, filter: Array<string> = null, paging: string = '18-0', sort: string = ''):
        Observable<{ids: number[], count: number}> {
        // ! Use Range for limit/offset calls - maintain state for pagination or infinite scroll
        const headers: HttpHeaders = new HttpHeaders({'Range': paging, 'X-Sort': sort});

        let ids: number[] = [];
        let count = 0;

        const url = `${this.apiV2URL}/catalogs/${windowID}`;
        return this.http.post<HttpResponse<Response>>(
            url,
            {filter: filter}, {observe: 'response', headers: headers}
        ).pipe(
            map(httpResponse => {

                const response = new Response(httpResponse.body);

                if (Array.isArray(response.data)) {
                    count = parseInt(httpResponse.headers.get('X-Count'), 10);
                    ids = response.data;
                    return {ids, count};

                }
                return {ids, count};
            }),
            catchError(this.handleError('ProductService::getCachedCatalogs', {ids, count}))
        );
    }



    public getCatalogsByIds(ids: number[]): Observable<ProductCatalog[]> {

        if (ids.length > 0) {
          const url = `${API_URL}${BASE_URL}/catalogs/for-ids`;
            return this.http.post<Response>(
                url, ids
            ).pipe(
                map(response => {
                    if (Array.isArray(response.data)) {
                        return response.data.map( i => new ProductCatalog(this.adaptV2CatalogItem(i)));
                    }
                    return [];
                }),
                catchError(this.handleError('ProductService::getUncachedCatalogs', []))
            );
        } else {
            return of([]);
        }
    }

    // The catalogs/for-ids endpoint moved from legacy Phalcon to Symfony (/v2). The v2 payload
    // differs in two ways the homepage programs grid relies on, so normalize it back to the
    // legacy shape before building ProductCatalog:
    //  - `content` comes as an array of {category, data}; ProductCatalog.getContent() expects it
    //    keyed by category ({ DEFAULT: ..., SUCCESS: ... }).
    //  - in_market_start_at/in_market_end_at/ship_at were promoted to top-level by Phalcon but v2
    //    only returns them inside attrs[]; promote them (noon UTC, mirroring the old DateTime).
    private adaptV2CatalogItem(item: V2CatalogItem): V2CatalogItem {
        if (!item) {
            return item;
        }

        if (Array.isArray(item.content)) {
            item.content = item.content.reduce((acc, entry) => {
                if (entry && entry.category) {
                    acc[entry.category] = entry.data;
                }
                return acc;
            }, {});
        }

        if (Array.isArray(item.attrs)) {
            ['in_market_start_at', 'in_market_end_at', 'ship_at'].forEach(name => {
                if (item[name] === undefined) {
                    const attr = item.attrs.find(a => a && a.attr === name);
                    if (attr && attr.value) {
                        item[name] = {date: `${attr.value} 12:00:00.000000`, timezone_type: 3, timezone: 'UTC'};
                    }
                }
            });
        }

        return item;
    }

    // return list of products info
    public getProductsShortInfo(windowID: number, slugOrID: string | number = null, orderId: number = 0,  filter: Array<string> = null,
                                paging: string = '18-0', sort: string = '', show: string = '', wslrEntityId = 0,
                                includeOutOfStock = false)
            : Observable<ProductResult> {

        const  headersBody = {'Range': paging};
        if (sort) {
            headersBody['X-Sort'] = sort;
        }
        if (show) {
            headersBody['X-Show'] = show;
        }

        const headers: HttpHeaders = new HttpHeaders(headersBody);

        let url: string;
        if (slugOrID) {
            url = `${API_URL}/es/products/catalog/${slugOrID}/short-info`;
        } else {
            url = `${API_URL}/es/products/window/${windowID}/short-info`;
        }

        const body = {filter: filter};
        if (wslrEntityId) {
            body['entity_id'] = wslrEntityId;
        }
        if (orderId) {
            body['order_id'] = orderId;
        }
        if (includeOutOfStock) {
            // Only send when true; absent / false both mean "hide OOS items" on the BE side
            // (PBIs 79734 + 74014). Keeps payload minimal for callers that never opt in.
            body['include_out_of_stock'] = true;
        }

        // AB#85232: cart items are pinned above unordered ones in LIST view only (44261 FR1
        // Clarification Addendum AC2/AC5). Always sent, both values, so the back end never has to
        // infer the view. An absent value means a client older than this ticket and keeps the
        // previous behaviour, which is what lets the two sides deploy independently.
        body['view'] = this.localStorageService.isProductListView() ? 'list' : 'grid';


        return this.http.post<HttpResponse<Response>>(
            url, body, {observe: 'response', headers: headers}
        ).pipe(
            map(httpResponse => {
                let count = 0;
                let products: Product[] = [];
                const response = new Response(httpResponse.body);
                if (Array.isArray(response.data)) {
                    count = parseInt(httpResponse.headers.get('X-Count'), 10);
                    products = response.data.map( i => new Product(i));
                }
                return {products, count};
            }),
            catchError(this.handleError('ProductService::getProductsShortInfo', {products: [], count: 0}))
        );
    }

    // return list of product ids
    public getProductsIds(windowID: number, slugOrID: string | number = null, filter: Array<string> = null,
                      paging: string = '18-0', sort: string = '', wslrEntityId = 0): Observable<{ids: number[], count: number}> {
        // ! Use Range for limit/offset calls - maintain state for pagination or infinite scroll
        const  headersBody = {'Range': paging};
        if (sort) {
            headersBody['X-Sort'] = sort;
        }
        const headers: HttpHeaders = new HttpHeaders(headersBody);

        let url: string;
        if (slugOrID) {
            // url = this.apiURL + '/catalog/' + slugOrID + '/items';
            url = `${API_URL}/es/products/catalog/${slugOrID}/items`;
        } else {
            // url  = `${API_URL}${BASE_URL}/window/${windowID}/items`;
            url = this.apiURL + '/window/' + windowID + '/items';
        }

        const body = {filter: filter};
        if (wslrEntityId) {
            body['entity_id'] = wslrEntityId;
        }

        return this.http.post<HttpResponse<Response>>(
            url, body, {observe: 'response', headers: headers}
        ).pipe(
            map(httpResponse => {
                let count = 0;
                let ids: number[] = [];

                const response = new Response(httpResponse.body);
                if (Array.isArray(response.data)) {
                    count = parseInt(httpResponse.headers.get('X-Count'), 10);
                    ids = response.data;

                }
                return {ids, count};
            }),
            catchError(this.handleError('ProductService::getCachedProducts', {ids: [], count: 0}))
        );
    }


    /**
     * Fetch products using either the full product response or the card-sized
     * brief response. The full response remains the default.
     */
    getProductsByIds(
        ids: number[],
        shape: ProductResponseShape = 'full'
    ): Observable<Product[]> {
        if (!ids?.length) {
            return of([]);
        }

        const brief = shape === 'brief';

        const url = `${API_URL}/es/products/for-ids`;
        const params = brief
            ? new HttpParams().set('shape', 'brief')
            : undefined;

        return this.http.post<Response>(url, ids, {params}).pipe(
            map(response =>
                Array.isArray(response.data)
                    ? response.data.map(item => new Product(item))
                    : []
            ),
            catchError(
                this.handleError(
                    'ApiProductService::getProductsByIds',
                    []
                )
            )
        );
    }

    /**
     *
     * @param productIds - array of product ids
     * @param windowID
     */
    public refreshAggregateQuantities(productIds: number[], windowID: number): Observable<AggregateQuantities> {

      const url = `${API_URL}${BASE_URL}/refresh-aggregate-quantities/${windowID}`;
        return this.http.post<Response>(url, productIds)
            .pipe(
                map( response => {
                    return response.data;
                }),
                catchError(this.handleError('ProductService::refreshAggregateQuantities', null))
            );
    }

    public toggleFavorite(product: Product): Observable<Product> {

        const cloneProduct = (p: Product) => {
            return new Product(p);
        }

        // const url = `${API_URL}${BASE_URL}/${product.id}`;
        const url = `${this.apiURL}/${product.id}`;
        return this.http.put<Response>(url, {favorite: !product.isFavorite})
            .pipe(
                map( response => {
                    if (response.data) {
                        const newProduct = cloneProduct(product);
                        newProduct.toggleFavorite();
                        return newProduct;
                    }
                    return product;
                }),
                catchError(this.handleError('ProductService::toggleFavorite', null))
            );
    }

    // POST: /api/products/favorites/{window_id:[0-9]+}/short-info
    public getFavoriteProducts(windowID: number, filter: Array<string> = null,
                          paging: string = '18-0', sort: string = '', show: string = '', wslrEntityId = 0):
            Observable<ProductResult> {
        // ! Use Range for limit/offset calls - maintain state for pagination or infinite scroll

        const  headersBody = {'Range': paging};
        if (sort) {
            headersBody['X-Sort'] = sort;
        }
        if (show) {
            headersBody['X-Show'] = show;
        }

        const headers: HttpHeaders = new HttpHeaders(headersBody);

        const url = this.apiV2URL + '/favorites/' + windowID + '/short-info';
        // const url = `${API_URL}${BASE_URL}/favorites/${windowID}/short-info`;

        const body = {filter: filter};
        if (wslrEntityId) {
            body['entity_id'] = wslrEntityId;
        }
        return this.http.post<HttpResponse<Response>>(
            url, body, {observe: 'response', headers: headers}
        ).pipe(
            map(httpResponse => {
                let count = 0;
                let products: Product[] = [];

                const response = new Response(httpResponse.body);
                if (Array.isArray(response.data)) {
                    count = parseInt(httpResponse.headers.get('X-Count'), 10);
                    products = response.data.map( d => new Product(d));
                }
                return {products, count};
            }),
            catchError(this.handleError('ProductService::getFavoriteProducts', {products: [], count: 0}))
        );
    }


    public fetchFavoritesFilters(orderWindowId =  0, filters: FilterGroup[] = []): Observable<FilterGroup[]> {
        if (isEmptyArray(filters)) {
          const url = `${this.apiV2URL}/favorites/${orderWindowId}/product-filters`;
            return this.http.get<Response>(url).pipe(
                map( response => {
                    if (Array.isArray(response.data)) {
                        return response.data.map( d => new FilterGroup(d));
                    }
                    return [];
                }),
                catchError(this.handleError('ProductService::fetchFavoritesFilters', []))
            );
        } else {
          const url = `${this.apiV2URL}/favorites/${orderWindowId}/product-filters`;
            return this.http.post<Response>(url,
                {filter: transformFilters(filters)}).pipe(
                map( response => {
                    if (Array.isArray(response.data)) {
                        return response.data.map( d => new FilterGroup(d));
                    }
                    return [];
                }),
                catchError(this.handleError('ProductService::fetchFavoritesFilters', []))
            );
        }

    }

    public getDecisionPointData(term: string, dpID: string= '', productId: number = 0): Observable<IDecisionPointData[]> {

        const  mapResponseToDPData = (data): IDecisionPointData => {
            const result: IDecisionPointData = {
                id: data.id || dpID,
                wslr_count: +data.wslr_count,
                store_count: +data.store_count,
            }
            if (data.label) {
                result.label = data.label;
            }

            if (data.eligible_wslr_count) {
                result.eligible_wslr_count = +data.eligible_wslr_count;
            }

            return result;
        }

        // const url = `${API_URL}${BASE_URL}/get-distribution-data`;
        const url = `${this.apiURL}/get-distribution-data`;
        return this.http.post<Response>(url, {
            term: term,
            decision_point_id: dpID,
            product_id: productId
        }, {headers: this.xsrfTokenHeader}).pipe(
            map(response => {
                if (response.data) {
                    if (Array.isArray(response.data)) {
                        return response.data.map( d => mapResponseToDPData(d));
                    } else {
                        return [mapResponseToDPData(response.data)];
                    }
                }

                return [];
            }),
            catchError(this.handleError('ProductService::getDecisionPointData', []))
        );
    }

    public calculateDecisionPoints(dpID: string, productID: number, quantity: number): Observable<IDecisionPointDelivery[]> {
      // const url = `${API_URL}${BASE_URL}/calculate-distribution`;
      const url = `${this.apiURL}/calculate-distribution`;
        return this.http.post<Response>(url, {
            decision_point_id: dpID,
            product_id: productID,
            quantity: quantity
        }, {headers: this.xsrfTokenHeader}).pipe(
            map(response => {
                return response.data;
            }),
            catchError(this.handleError('ProductService::calculateDecisionPoints', []))
        );
    }

    public validateProduct(productId: number, windowId: number ): Observable<boolean> {
      //const url = `${API_URL}${BASE_URL}/${productId}/window/${windowId}/check`;
      const url = `${this.apiURL}/${productId}/window/${windowId}/check`;

        return this.http.get<Response>(url)
            .pipe(
                map( response => {
                    return response.data
                }),
                catchError(this.handleError('ProductService::validateProduct', false))
            );
    }

    public fetchProductCategories(): Observable<ProductCategory[]> {
      // const url = `${API_URL}${BASE_URL}/catalogs/od-categories`;
      const url = `${this.apiURL}/catalogs/od-categories`;

        return this.http.get<Response>(url).pipe(
            map( result => {
                if (isEmptyArray(result.data)) {
                    return []
                }

                return result.data.map( item => new ProductCatalog(item));
            }),
            catchError(this.handleError('ProductService::fetchProductCategories', []))
        );

    }

    public fetchProductBrands(): Observable<Brand[]> {
      // const url = `${API_URL}${BASE_URL}/brands`;
      const url = `${this.apiURL}/brands`;
        return this.http.get<Response>(url).pipe(
            map( response => {
                if (!isEmptyArray(response.data)) {
                    return response.data.map( d => new Brand(d));
                }
                return [];
            }),
            catchError(this.handleError('ProductService::fetchRecentProducts', []))
        );
    }

    public fetchRecentProducts(filter: Array<string> = null, paging: string = '18-0', sort: string = ''):
        Observable<{ids: number[], count: number}> {
        const headers: HttpHeaders = new HttpHeaders({'Range': paging, 'X-Sort': sort});

        const body = {filter: filter};
        const url = `${this.apiV2URL}/recently-purchased`;
        return this.http.post<HttpResponse<Response>>(
            url, body, {observe: 'response', headers: headers}
        ).pipe(
            map(httpResponse => {
                let count = 0;
                let ids: number[] = [];

                const response = new Response(httpResponse.body);

                if (!isEmptyArray(response.data)) {
                    ids = response.data;

                    const headerCount = Number.parseInt(
                        httpResponse.headers.get('X-Count') ?? '',
                        10
                    );

                    count = Number.isNaN(headerCount)
                        ? ids.length
                        : headerCount;
                }
                return {ids, count};
            }),
            catchError(this.handleError('ProductService::fetchRecentProducts', {ids: [], count: 0}))
        );
    }

    public exportOrderedProducts(type: 'xlsx' | 'pdf',   windowID: number, slugOrID: string | number = null, filter: Array<string> = null,
                         sort: string = '', wslrEntityId = 0): Observable<boolean> {
        const  headersBody = {};
        if (sort) {
            headersBody['X-Sort'] = sort;
        }
        const headers: HttpHeaders = new HttpHeaders(headersBody);
        // const url = slugOrID ?
        //     `${API_URL}${BASE_URL}/catalog/${slugOrID}/short-info/download/${type}` :
        //     `${API_URL}${BASE_URL}/window/${windowID}/short-info/download/${type}`;

      const url = slugOrID ?
        `${this.apiURL}/catalog/${slugOrID}/short-info/download/${type}` :
        `${this.apiURL}/window/${windowID}/short-info/download/${type}`;

        const body = {filter: filter};
        if (wslrEntityId) {
            body['entity_id'] = wslrEntityId;
        }

        return this.http.post<Response>(
            url, body, {headers: headers}
        ).pipe(
            map(response => {
                return !!response.data
            }),
            catchError(this.handleError('ProductService::getCachedProducts', false))
        );
    }


    public fetchAllocationGroups(): Observable<AllocationGroup[]>  {
      const url = `${API_URL}/account/allocation-groups`;

      return this.http.get<Response>(url).pipe(
        map( response => {
          if (isEmptyArray(response.data)) {
            return [];
          }
          return response.data as AllocationGroup[];
        }),
        catchError(this.handleError('ProductService::fetchAllocationGroups', []))
      );
    }

    public fetchAllocationGroupForProduct(productId: number): Observable<InventoryAllocation[]> {
        const url = `${API_URL}/products/${productId}/allocations-groups`;

        return this.http.get<Response>(url).pipe(
            map( response => {
                if (isEmptyArray(response.data)) {
                    return [];
                }
                return response.data as InventoryAllocation[];
            }),
            catchError(this.handleError('ProductService::fetchAllocationGroupForProduct', []))
        );
    }

    /**
     * Returns each product's sku allocations keyed by product id. The endpoint answers with a flat
     * array of {product_id, allocation_groups}, which is regrouped here so callers index by product
     * the way the store does.
     */
    public fetchAllocationGroupForProducts(productIds: number[]): Observable<Record<number, InventoryAllocation[]>> {
        const url = `${API_URL}/products/allocations-groups`;

        const body = {product_ids: productIds};
        return this.http.post<Response>(url, body).pipe(
            map( response => {
                if (isEmptyArray(response.data)) {
                    return {};
                }

                return (response.data as ProductInventoryAllocations[]).reduce((byProductId, entry) => {
                    if (entry?.product_id) {
                        byProductId[entry.product_id] = entry.allocation_groups || [];
                    }
                    return byProductId;
                }, {} as Record<number, InventoryAllocation[]>);
            }),
            // Deliberately not handleError: that resolves to a default value, which would make a
            // failed batch indistinguishable from "this user has no allocation groups" for a whole
            // page of items. The store records the failure instead, and the dropdown says so.
            catchError(error => throwError(() => error))
        );
    }



  private handleError<T>(operation = 'operation', result?: T) {
        return (error: DetailedError): Observable<T> => {
            const errorMessage = findDetailedErrorMessage(error);
            if (errorMessage) {
                this.toastr.error(errorMessage);
                console.log(operation + ' failed', error);
            }
            return of(result as T);
        };
    }


    private get xsrfTokenHeader() {
        return new HttpHeaders({'x-xsrf-token': ''});
    }

}
